import { Localization } from "../hooks/useLocaleFormatter";
import { ProductFragment, ProductPriceFragment, ProductUnit, StaircaseAssemblyConfiguratorSupportFragment, StaircaseAssemblyConfiguratorSupportFragmentVariant, UUID } from "../model/fragments";
import { sumPriceArrays } from "./formatPrice";



// TODO: Refactor this to be a regular class without static methods
export class StaircaseAssemblyConfiguratorUtils
{
    private static assemblyPriceCzkMultiplier: number = 1.0;
    private static assemblyPriceEurSkMultiplier: number = 1.0;

    static setAssemblyPriceMultipliers(priceCzkMultiplier: number, priceEurSkMultiplier: number) {
        this.assemblyPriceCzkMultiplier = priceCzkMultiplier;
        this.assemblyPriceEurSkMultiplier = priceEurSkMultiplier;
    }

    static applyAssemblyPriceMultipliers(prices: ProductPriceFragment[]) {

        return prices.map(price => {
            if(price.currency === 'CZK') {
                return {
                    ...price,
                    cents: price.cents * StaircaseAssemblyConfiguratorUtils.assemblyPriceCzkMultiplier,
                };
            } else if(price.currency === 'EUR' && price.region === 'sk') {
                return {
                    ...price,
                    cents: price.cents * StaircaseAssemblyConfiguratorUtils.assemblyPriceEurSkMultiplier,
                };
            }

            return price; // No change for other currencies
        });
    }

    static getStaircaseStepCountForHeight(height: number, spacing: number, hasLanding: boolean) {
        return Math.ceil(height / spacing) - (hasLanding ? 1 : 0);
    }

    static getStaircaseStepPrice(localization: Localization, prices: ProductPriceFragment[], length: number, depth: number) {
        let extracted = localization.extractPrices(prices);
        extracted = this.applyAssemblyPriceMultipliers(extracted);
    
        return extracted.map(price => ({
            ...price, 
            cents: price.cents * length / (1000.0),
            unit: ProductUnit.item 
        } as ProductPriceFragment));
    }

    static getStaircaseTotalStepPrice(stepPrices: ProductPriceFragment[], stepCount: number)
    {
        return stepPrices.map(p => ({
            ...p,
            cents: p.cents * stepCount,
            unit: undefined
        }));
    }

    static getStaircaseStringerPricesWithoutHorizontal(localization: Localization, hasLanding: boolean, pricesFixed: ProductPriceFragment[], pricesStep: ProductPriceFragment[], stepCount: number)
    {
        let extractedFixed = localization.extractPrices(pricesFixed);
        let extractedHeight = localization.extractPrices(pricesStep);

        extractedFixed = this.applyAssemblyPriceMultipliers(extractedFixed);
        extractedHeight = this.applyAssemblyPriceMultipliers(extractedHeight);

        const fixedPrices = extractedFixed.map(price => ({
            ...price, 
            cents: price.cents,
            unit: ProductUnit.item 
        } as ProductPriceFragment));

        const heightPrices = extractedHeight.map(price => ({
            ...price, 
            cents: price.cents * (stepCount - (hasLanding ? 1 : 2)),
            unit: ProductUnit.item 
        } as ProductPriceFragment));

        return sumPriceArrays([fixedPrices, heightPrices]);
    }

    static getStaircaseStringerPrices(localization: Localization, hasLanding: boolean, pricesFixed: ProductPriceFragment[], pricesStep: ProductPriceFragment[], pricesAlt: ProductPriceFragment[], pricesAltMin: ProductPriceFragment[], stepCount: number, landingDepth: number)
    {
        let extractedFixed = localization.extractPrices(pricesFixed);
        let extractedHeight = localization.extractPrices(pricesStep);
        let extractedDepth  = localization.extractPrices(pricesAlt);
    
        extractedFixed = this.applyAssemblyPriceMultipliers(extractedFixed);
        extractedHeight = this.applyAssemblyPriceMultipliers(extractedHeight);
        extractedDepth  = this.applyAssemblyPriceMultipliers(extractedDepth);

        const fixedPrices = extractedFixed.map(price => ({
            ...price, 
            cents: price.cents,
            unit: ProductUnit.item 
        } as ProductPriceFragment));

        const heightPrices = extractedHeight.map(price => ({
            ...price, 
            cents: price.cents * (stepCount - (hasLanding ? 1 : 2)),
            unit: ProductUnit.item 
        } as ProductPriceFragment));

        let depthPrices : ProductPriceFragment[] = [];
        if(hasLanding)
        {
            depthPrices = extractedDepth.map(price => ({
                ...price, 
                // Vzdy odecist 50mm navic
                cents: Math.max(0, price.cents * ((landingDepth - 50.0) / 1000.0)),
                unit: ProductUnit.item 
            } as ProductPriceFragment));
        }
        else 
        {
            depthPrices = extractedDepth.map(price => ({
                ...price, 
                cents: pricesAltMin.find(pr => pr.region === price.region && pr.currency === price.currency)?.cents ?? 0,
                unit: ProductUnit.item 
            } as ProductPriceFragment));

        }

        return sumPriceArrays([fixedPrices, heightPrices, depthPrices]);
    }

    static getStaircaseTotalStringerPrice(stringerPrices: ProductPriceFragment[], stringerCount: number)
    {
        return stringerPrices.map(p => ({
            ...p,
            cents: p.cents * stringerCount,
            unit: undefined
        }));
    }

    static getStaircaseLandingPrices(localization: Localization, pricesVolume: ProductPriceFragment[], pricesLength: ProductPriceFragment[], landingLength: number, landingDepth: number, landingHeight: number)
    {
        let extractedVolume = localization.extractPrices(pricesVolume);
        let extractedLength = localization.extractPrices(pricesLength);

        extractedVolume = this.applyAssemblyPriceMultipliers(extractedVolume);
        extractedLength = this.applyAssemblyPriceMultipliers(extractedLength);
    
        const volumePrices = extractedVolume.map(price => ({
            ...price, 
            cents: price.cents * landingLength * landingDepth * landingHeight / (1000.0*1000.0*1000.0),
            unit: undefined
        } as ProductPriceFragment));

        const lengthPrices = extractedLength.map(price => ({
            ...price, 
            cents: price.cents * landingLength / 1000.0,
            unit: undefined
        } as ProductPriceFragment));

        return sumPriceArrays([volumePrices, lengthPrices]);
    }

    static getStaircaseSupportPrices(localization: Localization, hasLanding: boolean, support: StaircaseAssemblyConfiguratorSupportFragment, stepCount: number, stepLength: number)
    {
        const variant = support.variants.reduce((acc : StaircaseAssemblyConfiguratorSupportFragmentVariant | null, v: StaircaseAssemblyConfiguratorSupportFragmentVariant) => 
            v.minLength <= stepLength && (!acc || v.minLength > acc!.minLength) ? v : acc
        , null);

        if(!variant)
        {
            return null;
        }

        let extractedBase2 = localization.extractPrices(variant.pricesBase2);
        let extractedBase3 = localization.extractPrices(variant.pricesBase3);
        let extractedStep = localization.extractPrices(variant.prices);

        extractedBase2 = this.applyAssemblyPriceMultipliers(extractedBase2);
        extractedBase3 = this.applyAssemblyPriceMultipliers(extractedBase3);
        extractedStep = this.applyAssemblyPriceMultipliers(extractedStep);

        if(stepCount <= 2)
        {
            return extractedBase2;
        }
        
        const stepCountPrices = extractedStep.map(price => ({
            ...price, 
            cents: price.cents * (stepCount - (hasLanding ? 2 : 3)),
            unit: ProductUnit.item 
        } as ProductPriceFragment));

        //console.log('SUPPORT', stepCount + (hasLanding ? 1 : 0), (stepCount - (hasLanding ? 2 : 3)), extractedBase3, stepCountPrices, sumPriceArrays([extractedBase3, stepCountPrices]));
        return sumPriceArrays([extractedBase3, stepCountPrices]);
    }

    static getStaircaseTotalSupportPrice(supportPrices: ProductPriceFragment[], supportCount: number)
    {
        return supportPrices.map(p => ({
            ...p,
            cents: p.cents * supportCount,
            unit: undefined
        }));
    }

    static determineSupportCount(hasLanding: boolean, stringerMaxDepthsByStepLengths: [number, number][], landingThreshold: number, landingLength: number, landingDepth: number, stepCount: number, stepDepth: number, stepLength: number)
    {
        if(!hasLanding)
        {
            return 1;
        }

        const arr = stringerMaxDepthsByStepLengths.find(arr => arr[0] > stepLength);
        if(!arr)
        {
            return 1;
        }

        const maxDepth = arr[1];
        const totalDepth = stepCount * (stepDepth - 50.0) + landingDepth;
        
        if(landingLength > (stepLength + landingThreshold))
        {
            // Sirkove kriterium
            return 2;
        }
        else if(totalDepth > (maxDepth + (stepDepth - 50.0)))
        {
            // Kriterium maximalni hloubky
            return 2;
        }

        return 1;
    }
}


export class StaircaseStepsConfiguratorUtils 
{
    static getPriceForStraightStep(product: ProductFragment, desiredLength: number | undefined, variantId: UUID | undefined, correction: ProductPriceFragment[], unit: boolean)
    {
        if(!desiredLength || !variantId)
        {
            return [];
        }

        const corr = correction.length ? correction[0] : { cents: 0};
        return product.variants.find(v => v.id === variantId)?.prices.map(p => ({
            ...p,
            cents: p.cents * desiredLength / 1000.0 - ((desiredLength - 1000.0) / 1000.0) * corr.cents,
            unit: unit ? ProductUnit.item : undefined
        })) ?? [];
    }

    static determineRadiusStageAvailability(stage: string, radiusType: string, selection: {[prop: string]: boolean})
	{
        const selectedCount = Object.values(selection).filter(s => s).length;
        const previousStage = 
            stage == '1' ? radiusType :
            (
                stage == radiusType ? null :
                (parseInt(stage) - 1).toString()
            );

        const nextStage = 
            stage == '10' ? null :
            (
                stage == radiusType ? '1' :
                (parseInt(stage) + 1).toString()
            );


        const previous = previousStage ? selection[previousStage] : false;
        const next = nextStage ? selection[nextStage] : false;
        return selectedCount == 0 || (next != previous) || (selectedCount == 1 && selection[stage]);
	}

}